import type { McpConsentInfo, McpContainerMode, McpGrant, UpdateMcpGrant } from '@brain/contracts'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './api.ts'

/** MCP: consent page + "Verbundene KI-Clients" (§13.1, ADR 0009). */

/**
 * Location of the first page load, captured before the router starts (imported first in main.tsx): the router
 * re-serializes search params, but Better Auth's signed query (repeated `ba_param`, `sig`) must stay byte-identical.
 */
export const initialLocation =
  typeof window === 'undefined'
    ? { pathname: '', search: '' }
    : { pathname: window.location.pathname, search: window.location.search }

/** The raw query for an /oauth/* page that was opened directly by a server redirect. */
export function rawOAuthQuery(pathname: string): string {
  return initialLocation.pathname === pathname ? initialLocation.search.replace(/^\?/, '') : ''
}

export const mcpGrantsQuery = () =>
  queryOptions({
    queryKey: ['mcp-grants'] as const,
    queryFn: async () => (await api.get<{ grants: McpGrant[] }>('/v1/mcp/grants')).grants,
  })

export const mcpConsentQuery = (oauthQuery: string) =>
  queryOptions({
    queryKey: ['mcp-consent', oauthQuery] as const,
    queryFn: () => api.get<McpConsentInfo>(`/v1/mcp/consent?oauth_query=${encodeURIComponent(oauthQuery)}`),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  })

export interface ScopeChoice {
  containerMode: McpContainerMode
  containerIds: string[]
  canWrite: boolean
}

export function submitConsent(oauthQuery: string, accept: boolean, choice: ScopeChoice) {
  return api.post<{ url: string }>('/v1/mcp/consent', { oauthQuery, accept, ...choice })
}

export function useUpdateMcpGrant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, patch }: { clientId: string; patch: UpdateMcpGrant }) =>
      api.patch<McpGrant>(`/v1/mcp/grants/${encodeURIComponent(clientId)}`, patch),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['mcp-grants'] }),
  })
}

export function useRevokeMcpGrant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (clientId: string) => api.delete(`/v1/mcp/grants/${encodeURIComponent(clientId)}`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['mcp-grants'] }),
  })
}

/** Better Auth sends the browser to /oauth/login with the signed authorize query; after the login the
 * original authorize request is replayed (the session then exists). The signature params are dropped. */
export function authorizeContinuation(search: string): string | null {
  const params = new URLSearchParams(search)
  if (!params.get('client_id') || !params.get('response_type')) return null
  for (const k of [...params.keys()]) if (k === 'sig' || k === 'exp' || k.startsWith('ba_')) params.delete(k)
  // the login just happened – replaying prompt=login would send the browser back to the login page
  if (params.get('prompt') === 'login') params.delete('prompt')
  return `/auth/oauth2/authorize?${params.toString()}`
}

export const isAuthorizeUrl = (target: string) => target.startsWith('/auth/oauth2/authorize?')
